import { type ChangeEvent, useRef } from "react";
import { Row } from "../ComponentLayout/Row";
import { TextInput } from "./TextInput";
import {
  formatPhoneNumber,
  unformatPhoneNumber,
} from "../Helpers/formatPhoneNumber";
import {
  countSignificantBefore,
  useFormattedCaret,
} from "./Internal/useFormattedCaret";
import type { InputWidth } from "./inputWidth";

interface PhoneInputProps {
  /** Raw digits, no formatting. Displayed formatted as "(778) 563-5684". */
  phoneNumber?: string | undefined;
  /** Raw extension digits. */
  extension?: string | undefined;
  onPhoneNumberChange: (phoneNumber: string) => void;
  onExtensionChange: (extension: string) => void;
  phonePlaceholder?: string | undefined;
  extensionPlaceholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Width of the phone field; the extension stays compact. Defaults to full. */
  width?: InputWidth | undefined;
  /**
   * `data-testid` for the number field; the extension gets `${testId}-extension`.
   * Worth setting when the fixed `name` below isn't specific enough to aim at —
   * it's the same on every phone field in the app.
   */
  testId?: string | undefined;
  /**
   * Render only the number field, for phone numbers that never carry an
   * extension (e.g. a 24-hour ERAP activation line).
   */
  hideExtension?: boolean | undefined;
}

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

// A leading 1 is the North-American country code, so allow 11 digits; otherwise
// cap at the 10-digit local number.
function capPhoneDigits(raw: string): string {
  return raw.startsWith("1") ? raw.slice(0, 11) : raw.slice(0, 10);
}

function sanitizeExtension(raw: string): string {
  return unformatPhoneNumber(raw).slice(0, 6);
}

// Remove the n-th digit (1-indexed) from an all-digit string.
function removeNthDigit(digits: string, n: number): string {
  return digits.slice(0, n - 1) + digits.slice(n);
}

/**
 * A phone number + extension pair. The number is formatted as you type
 * ("(778) 563-5684") while `phoneNumber`/`extension` are stored as raw digits.
 * Built on two `TextInput`s, so it inherits the active control presentation
 * (boxed in dense admin screens, underlined in the spacious customer flows) and
 * the FormItem error state automatically — wrap it in a `FormItem` for a label.
 *
 * The number field preserves the caret across reformatting (see
 * `useFormattedCaret`), so mid-string edits and backspacing over the spaces /
 * parens behave the way you'd expect.
 */
export function PhoneInput({
  phoneNumber,
  extension,
  onPhoneNumberChange,
  onExtensionChange,
  phonePlaceholder = "Phone number",
  extensionPlaceholder = "Ext.",
  disabled,
  width = "full",
  testId,
  hideExtension,
}: PhoneInputProps) {
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const phoneRef = useRef<HTMLInputElement>(null);
  const formatted = formatPhoneNumber(phoneNumber);
  const setCaretTarget = useFormattedCaret(phoneRef, formatted, isDigit);

  function handlePhoneChange(e: ChangeEvent<HTMLInputElement>) {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    const digitsBeforeCaret = countSignificantBefore(el.value, caret, isDigit);
    const newDigits = unformatPhoneNumber(el.value);
    const oldDigits = phoneNumber ?? "";

    // Backspacing over a formatting character (the space in "(778) ", a paren)
    // leaves the digits unchanged but the string shorter. Delete the digit just
    // before the caret instead, so the backspace actually makes progress rather
    // than the formatter re-inserting the character and stalling.
    if (
      newDigits === oldDigits &&
      el.value.length < formatted.length &&
      digitsBeforeCaret > 0
    ) {
      setCaretTarget(digitsBeforeCaret - 1);
      onPhoneNumberChange(
        capPhoneDigits(removeNthDigit(oldDigits, digitsBeforeCaret)),
      );
      return;
    }

    setCaretTarget(digitsBeforeCaret);
    onPhoneNumberChange(capPhoneDigits(newDigits));
  }

  return (
    <Row gap={2}>
      <TextInput
        ref={phoneRef}
        // Fixed field names carried over from the Cargo phone input; most e2e
        // selectors (`[name='contact_phone_number_phone']`) rely on them. A
        // screen with two phone fields must pass `testId` to tell them apart.
        name="contact_phone_number_phone"
        type="tel"
        value={formatted}
        onChange={handlePhoneChange}
        placeholder={phonePlaceholder}
        disabled={disabled}
        width={width}
        {...(testId !== undefined && { "data-testid": testId })}
      />
      {!hideExtension && (
        <TextInput
          name="contact_phone_number_extension"
          type="tel"
          value={extension ?? ""}
          onChange={(e) => onExtensionChange(sanitizeExtension(e.target.value))}
          placeholder={extensionPlaceholder}
          disabled={disabled}
          width="xs"
          {...(testId !== undefined && {
            "data-testid": `${testId}-extension`,
          })}
        />
      )}
    </Row>
  );
}
